import { ModalTypes } from '@/components/modal-map'

// Native download function to replace downloadjs
export const downloadDataURI = (dataURI: string, filename: string): void => {
  const link = document.createElement('a')
  link.href = dataURI
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const readFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = reject
    reader.onabort = reject
    reader.onload = () => {
      resolve(reader.result as string)
    }
    reader.readAsDataURL(file)
  })
}

export const toBlob = (content: string) => {
  const byteCharacters = window.atob(content)
  const ab = new ArrayBuffer(byteCharacters.length)
  const ia = new Uint8Array(ab)

  for (let i = 0; i < byteCharacters.length; i++) {
    ia[i] = byteCharacters.charCodeAt(i)
  }

  return new Blob([ab])
}

// Get file extension by file name
export const getFileExtension = (filename = ''): string => {
  return ((filename || '').split('.').pop() || '').toLowerCase()
}

export const base64ToArrayBuffer = (_base64Str: string): Uint8Array => {
  const binaryString = window.atob(_base64Str)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  return bytes
}

export const decodeBase64ToString = (base64Str: string): string => {
  const bytes = base64ToArrayBuffer(base64Str)
  const decoder = new TextDecoder('utf-8')
  return decoder.decode(bytes)
}

export const toBytes = (content: string | ArrayBuffer | Uint8Array): Uint8Array => {
  if (typeof content === 'string') return new TextEncoder().encode(content)
  if (content instanceof Uint8Array) return content
  return new Uint8Array(content)
}

export const toBase64 = (bytes: Uint8Array): string => {
  const CHUNK = 8192
  let binary = ''
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

export const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const ab = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(ab).set(bytes)
  return ab
}

// https://itechowl.wordpress.com/2020/01/27/javascript-not-allowed-to-navigate-top-frame-to-data-url-chrome/

export const showPDFDocument = (_base64Str: string): void => {
  const bytes = base64ToArrayBuffer(_base64Str)
  const blob = new Blob([toArrayBuffer(bytes)], { type: 'application/pdf' })
  window.open(URL.createObjectURL(blob), '_blank')
}
// Function to check if the file type is supported for preview
export const canPreviewFile = (filename: string): boolean => {
  if (!filename) {
    return false // Return false if the filename is null or undefined
  }

  const fileExtension = filename.split('.').pop()?.toLowerCase() // Get the file extension

  switch (fileExtension) {
    case 'docx':
    case 'pdf':
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'xlsx':
    case 'xls':
    case 'json':
    case 'xml':
    case 'eml':
      return true // Supported file types
    default:
      return false // Unsupported file types
  }
}

// Main function to show the document based on its type
export const showDocument = (
  _base64Str: string,
  filename: string,
  showModal?: (type: ModalTypes, data: { content: string; fileName: string }) => void,
): void => {
  if (!canPreviewFile(filename)) {
    return // If file cannot be previewed, do nothing
  }

  const fileExtension = getFileExtension(filename)
  let mimeType: string | undefined

  // Handle modal previews for Excel files if showModal is provided
  if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    if (showModal) {
      showModal(ModalTypes.EXCEL_PREVIEW, {
        content: _base64Str,
        fileName: filename,
      })
    }
    return
  }

  // Handle modal previews for Docx files if showModal is provided
  if (fileExtension === 'docx') {
    if (showModal) {
      showModal(ModalTypes.DOCX_PREVIEW, {
        content: _base64Str,
        fileName: filename,
      })
    }
    return
  }

  // Handle modal previews for JSON files if showModal is provided
  if (fileExtension === 'json') {
    if (showModal) {
      showModal(ModalTypes.JSON_PREVIEW, {
        content: _base64Str,
        fileName: filename,
      })
    }
    return
  }

  // Handle modal previews for XML files if showModal is provided
  if (fileExtension === 'xml') {
    if (showModal) {
      showModal(ModalTypes.XML_PREVIEW, {
        content: _base64Str,
        fileName: filename,
      })
    }
    return
  }

  // Handle modal previews for EML files if showModal is provided
  if (fileExtension === 'eml') {
    if (showModal) {
      showModal(ModalTypes.EML_PREVIEW, {
        content: _base64Str,
        fileName: filename,
      })
    }
    return
  }

  // Handle direct preview for PDFs and images
  switch (fileExtension) {
    case 'pdf':
      mimeType = 'application/pdf'
      break
    case 'png':
      mimeType = 'image/png'
      break
    case 'jpg':
    case 'jpeg':
      mimeType = 'image/jpeg'
      break
  }

  // Convert base64 string to ArrayBuffer for blob preview
  const bytes = base64ToArrayBuffer(_base64Str)
  const blob = new Blob([toArrayBuffer(bytes)], { type: mimeType })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

export const downloadDocument = (fileName: string, content: string): void => {
  const dataURI = `data:application/octet-stream;base64,${content}`
  downloadDataURI(dataURI, fileName)
}

export const downloadJSONDocument = (fileName: string, content: string): void => {
  const dataURI = `data:text/json;charset=utf-8,${content}`
  downloadDataURI(dataURI, fileName)
}

export const downloadXMLDocument = (fileName: string, content: string): void => {
  const dataURI = `data:application/octet-stream;base64,${content}`
  downloadDataURI(dataURI, fileName)
}

export const downloadZip = (fileName: string, content: string): void => {
  const dataURI = `data:application/octet-stream;base64,${content}`
  downloadDataURI(dataURI, fileName)
}
